/**
 * ASCII Art Generator - export type definitions
 */

import type {
  AnimationType,
  BrailleVariant,
  CharsetType,
  ColorMode,
  DitherType,
  HalftoneShape,
  RetroDuotone,
  StyleType,
  TerminalCharset,
} from '../types'
import type { EffectType, EffectsState } from '../effects/types'
import type {
  FitMode,
  GifResolutionPreset,
  ResolvedExportDimensions,
} from './ResolutionPresets'
import {
  normalizeGifResolutionOptions,
} from './ResolutionPresets'

// ============================================
// Export formats
// ============================================

export type ExportFormat =
  | 'image'
  | 'gif'
  | 'html-js'
  | 'react'

export type ImageFormat = 'png' | 'jpg' | 'webp'
export type HtmlJsOutputMode = 'inline' | 'split'
export type ImageEmbedMode = 'embed' | 'external'
export type GifDitherMode = 'none' | 'floyd-steinberg' | 'ordered'

// ============================================
// Configs
// ============================================

export interface ImageExportConfig {
  format: ImageFormat
  quality: number
  scale: number
  width?: number
  height?: number
  backgroundColor?: string
}

export const DEFAULT_IMAGE_EXPORT_CONFIG: ImageExportConfig = {
  format: 'png',
  quality: 85,
  scale: 1,
  backgroundColor: '#000000',
}

export interface GifExportConfig {
  fps: number
  duration: number
  quality: number
  loop: number
  scale: number
  width?: number
  height?: number
  resolutionPreset: GifResolutionPreset
  fitMode: FitMode
  maxColors: 32 | 64 | 128 | 256
  dither: GifDitherMode
}

export const DEFAULT_GIF_EXPORT_CONFIG: GifExportConfig = {
  fps: 15,
  duration: 3,
  quality: 10,
  loop: 0,
  scale: 1,
  resolutionPreset: 'source',
  fitMode: 'contain',
  maxColors: 256,
  dither: 'floyd-steinberg',
}

export type GifExportStatus =
  | 'idle'
  | 'capturing'
  | 'rendering_frames'
  | 'encoding'
  | 'completed'
  | 'cancelled'
  | 'error'

export interface NormalizedGifExportConfig extends Omit<GifExportConfig, 'width' | 'height' | 'fitMode'>, ResolvedExportDimensions {
  totalFrames: number
  frameDelayMs: number
}

export interface HtmlJsExportConfig {
  outputMode: HtmlJsOutputMode
  imageEmbedMode: ImageEmbedMode
  externalImageUrl?: string
  engine: StyleType
  animation: AnimationType
  effects: EffectType[]
  effectsConfig: Partial<EffectsState>
  transparent: boolean
  alphaMask: boolean
  pauseOffScreen: boolean
  interaction: boolean
  fadeIn: boolean
  adaptivePerf: boolean
  hideDefaultContent?: boolean
}

export const DEFAULT_HTMLJS_EXPORT_CONFIG: HtmlJsExportConfig = {
  outputMode: 'inline',
  imageEmbedMode: 'embed',
  engine: 'classic',
  animation: 'wave',
  effects: [],
  effectsConfig: {},
  transparent: true,
  alphaMask: false,
  pauseOffScreen: true,
  interaction: true,
  fadeIn: false,
  adaptivePerf: false,
  hideDefaultContent: true,
}

export interface ReactExportConfig {
  componentName: string
  includeTypes: boolean
  splitRuntime: boolean
  engine: StyleType
  animation: AnimationType
  effects: EffectType[]
  effectsConfig: Partial<EffectsState>
  transparent: boolean
  alphaMask: boolean
  pauseOffScreen: boolean
  interaction: boolean
  fadeIn: boolean
  adaptivePerf: boolean
}

export const DEFAULT_REACT_EXPORT_CONFIG: ReactExportConfig = {
  componentName: 'AsciiBackground',
  includeTypes: true,
  splitRuntime: false,
  engine: 'classic',
  animation: 'wave',
  effects: [],
  effectsConfig: {},
  transparent: true,
  alphaMask: false,
  pauseOffScreen: true,
  interaction: true,
  fadeIn: false,
  adaptivePerf: false,
}

// ============================================
// Runtime config
// ============================================

export interface AsciiBgRuntimeConfig {
  engine: StyleType
  animation: AnimationType
  animationParams?: {
    amplitude?: number
    frequency?: number
    speed?: number
  }
  effects?: EffectType[]
  effectsConfig?: Partial<EffectsState>
  transparent?: boolean
  alphaMask?: boolean
  alphaMaskParams?: {
    radius: number
    softness: number
  }
  pauseOffScreen?: boolean
  interaction?: boolean
  fadeIn?: boolean
  adaptivePerf?: boolean
  targetFPS?: number
  fontSize?: number
  charSpacing?: number
  spacing?: number
  contrast?: number
  brightness?: number
  opacity?: number
  invert?: boolean
  backgroundColor?: string
  colorMode?: ColorMode
  customColor?: string
  halftoneShape?: HalftoneShape
  ditherStrength?: number
  ditherType?: DitherType
  ditherColorNum?: number
  halftoneSize?: number
  halftoneRotation?: number
  particleDensity?: number
  particleChar?: string
  claudeDensity?: number
  retroDuotone?: RetroDuotone
  retroNoise?: number
  lineLength?: number
  lineWidth?: number
  lineThickness?: number
  lineRotation?: number
  charset?: CharsetType
  customCharset?: string
  brailleVariant?: BrailleVariant
  terminalCharset?: TerminalCharset
  imageWidth?: number
  imageHeight?: number
  mouseInteractionMode?: 'attract' | 'push'
  ditherMouseRadius?: number
  ditherMouseInteractionEnabled?: boolean
  ditherWaveAmplitude?: number
  ditherWaveFrequency?: number
  ditherWaveSpeed?: number
  ditherWaveColor?: string
  dither2MouseInteractionEnabled?: boolean
  dither2MouseRadius?: number
  dither2AnimationEnabled?: boolean
  dither2WaveAmplitude?: number
  dither2WaveFrequency?: number
  dither2WaveSpeed?: number
  dither2WaveColor?: string
  dither2ColorNum?: number
  dither2PixelSize?: number
  hoverStrength?: number
  mouseAreaSize?: number
  mouseSpread?: number
  animationEnabled?: boolean
  mouseInteractionEnabled?: boolean
  mouseInfluenceRadius?: number
  mouseInfluenceStrength?: number
  renderFont?: string
}

export const DEFAULT_RUNTIME_CONFIG: AsciiBgRuntimeConfig = {
  engine: 'classic',
  animation: 'wave',
  transparent: true,
  alphaMask: false,
  pauseOffScreen: true,
  interaction: true,
  fadeIn: false,
  adaptivePerf: false,
  targetFPS: 60,
}

// ============================================
// Results and progress
// ============================================

export interface ExportResult {
  success: boolean
  filename: string
  size: number
  blob?: Blob
  content?: string
  externalJsContent?: string
  externalJsFilename?: string
  needsRuntimeDownload?: boolean
  typesContent?: string
  error?: string
  timestamp: number
}

export interface ExportProgress {
  status:
    | 'idle'
    | 'preparing'
    | 'capturing'
    | 'rendering_frames'
    | 'encoding'
    | 'muxing'
    | 'completed'
    | 'cancelled'
    | 'error'
  progress: number
  currentFrame?: number
  totalFrames?: number
  message?: string
}

export interface ExportHistoryItem {
  id: string
  format: ExportFormat
  config: ImageExportConfig | GifExportConfig | HtmlJsExportConfig | ReactExportConfig
  result: ExportResult
  createdAt: number
}

// ============================================
// Normalization helpers
// ============================================

export function normalizeGifConfig(
  config: Partial<GifExportConfig> = {},
  sourceSize?: { width: number; height: number }
): NormalizedGifExportConfig {
  const merged = { ...DEFAULT_GIF_EXPORT_CONFIG, ...config }
  const dimensions = normalizeGifResolutionOptions({
    preset: merged.resolutionPreset,
    width: merged.width,
    height: merged.height,
    scale: merged.scale,
    fitMode: merged.fitMode,
    sourceWidth: sourceSize?.width,
    sourceHeight: sourceSize?.height,
  })

  return {
    ...merged,
    ...dimensions,
    totalFrames: Math.max(1, Math.round(merged.fps * merged.duration)),
    frameDelayMs: Math.round(1000 / merged.fps),
  }
}

// ============================================
// Download helpers
// ============================================

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

export function downloadText(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'text/plain' })
  downloadBlob(blob, filename)
}

export function generateFilename(
  format: ExportFormat,
  imageFormat?: ImageFormat
): string {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, '')
  const prefix = 'ascii-art'

  switch (format) {
    case 'image':
      return `${prefix}_${timestamp}.${imageFormat || 'png'}`
    case 'gif':
      return `${prefix}_${timestamp}.gif`
    case 'html-js':
      return `${prefix}_${timestamp}.html`
    case 'react':
      return `${prefix}_${timestamp}.tsx`
    default:
      return `${prefix}_${timestamp}`
  }
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

export type {
  FitMode,
  GifResolutionPreset,
  ResolvedExportDimensions,
} from './ResolutionPresets'
